import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    useAuditIsReadonly,
    useExclusionDatePicker,
    usePendingCountApproval,
    useSharedAsset
} from "../../../../common/hooks";
import { Alert, Box, Chip, Typography } from "@mui/material/";
import { strings } from "../../../../common/i18n";
import { enums, helpers } from "../../../../common/lib";
import actions from "../../../../Store/actions";
import { routePrefix } from "../../Routes";
import Layout from "../Layout";
import Toolbar from "../Toolbar";
import NotFound from "../../../../common/components/NotFound";
import useCurrentAssetData from "./useCurrentAssetData";
import PendingCountApprovalDialog from "../PendingCountApprovalDialog";
import ActionComponent from "./LayoutComponents/ActionComponent";
import LeftComponent from "./LayoutComponents/LeftComponent";
import RightComponent from "./LayoutComponents/RightComponent";
import CircularProgress from "@mui/material/CircularProgress";
import styles from "./styles";
import { v4 as uuidv4 } from "uuid";
import { logToSentry } from "../../../../config/Sentry";
/**
 * Audit Asset Component
 */

export default () => {
    const dispatch = useDispatch();
    let navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const { auditId, auditAssetId } = params;

    const readonly = useAuditIsReadonly(auditId);
    const [assetAuditor, setAssetAuditor] = useState(null);
    const [validatedAsset, setValidatedAsset] = useState(null);
    const [loading, setLoading] = useState(true);
    const [buttonLoading, setButtonLoading] = useState(false);
    const [displayRejection, setDisplayRejection] = useState(false);
    const [exclusionReason, setExclusionReason] = useState(null);
    const [selectedDate, setSelectedDate] = useState(Date.now());
    const [exclusionNote, setExclusionNote] = useState("");
    const [historyNote, setHistoryNote] = useState("");
    const [noteType, setNoteType] = useState("exclusion");
    const isSharedAsset = useSharedAsset();
    const { guardApproval, guardReject, dialogProps } = usePendingCountApproval();

    const assets = useSelector((state) => state.assets);
    const auditAssets = useSelector((state) => state.auditAssets);
    const auditControllers = useSelector((state) => state.auditControllers);
    const auth = useSelector((state) => state.auth);
    const auditReview = useSelector((state) => state.auditReview);
    const typesList = useSelector((state) => state.typesList);
    const validatedVisibility = useSelector((state) => state.validatedVisibility);

    /** Check if the exclusionReason id is sold  */
    const exclusionDatePicker = useExclusionDatePicker(exclusionReason);

    /** check if asset is sold  */
    const assetSold = useMemo(() => {
        if (
            assetAuditor &&
            assetAuditor.exclusionReason &&
            Object.keys(assetAuditor.exclusionReason) &&
            assetAuditor.exclusionReason.name === "asset is sold"
        )
            return true;
        else return false;
    }, [assetAuditor]);

    /** On date change  */
    const handleDateChange = (date) => {
        const dt = date ? date.$d : new Date();
        const currentDate = new Date(dt);
        setSelectedDate(currentDate);
    };

    const data = useMemo(() => {
        if (auditAssetId && auditAssets.data.length) {
            const next = helpers.getListItemForId(auditAssets, auditAssetId);
            return {
                ...next,
                asset: helpers.getListItemForId(assets, next.asset)
            };
        }
        return null;
    }, [auditAssets, auditAssetId, assets]);

    useEffect(() => { setLoading(true); }, [auditAssetId]);
    useEffect(() => {
        if (data !== null) setLoading(false);
    }, [data]);

    /** On navigation */
    const handleNavigate = (previous) => () => {
        try {
            if (auditAssetId) {
                const id = Number(auditAssetId);
                const { ids } = auditReview.auditAssets;
                let index = ids.findIndex((i) => i === id);
                if (previous) {
                    index = index > 0 ? index - 1 : ids.length - 1;
                } else {
                    index = index < ids.length - 1 ? index + 1 : 0;
                }
                const next = ids[index];
                setAssetAuditor(null);
                const { pathname } = location;
                navigate(pathname.replace(/assets\/\d+/, `assets/${next}`));
            }
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On navigating asset:", { error });
        }
    };

    /* On patch success  */
    const patchSuccess = () => {
        try {
            if (auditReview.auditAssets.ids.length <= 1)
                auditId && navigate(`/${routePrefix}/${auditId}`);
            else handleNavigate(false)();
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            dispatch(actions.toast.create.error(strings("errors.onLoadAsset")));
            logToSentry("On patch success:", { error });
        }
    };
    /** On approve. While the audit is still being counted this is held until the
     * controller acknowledges the disclaimer; otherwise it runs straight away. */
    const onApprove = () => guardApproval(approveAsset);

    const approveAsset = async () => {
        try {
            setButtonLoading(true);
            const next = {
                id: data.id,
                isApproved: true,
                isAssigned: data.isAssigned,
                reissuedStatus: data.reissuedStatus
                    ? enums.status.reissued.resolved
                    : null,
                updatedBy: auth.account.id,
                isSold: assetSold,
                assetAuxiliaryId: data.asset.id
            };
            await dispatch(actions.auditAssets.create.patchRequest([next]));
            dispatch(actions.toast.create.success(strings("assetApproved")));
            setButtonLoading(false);
            patchSuccess();
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On approving asset:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onApproveAsset"))
            );
            setButtonLoading(false);
        }
    };

    /** On rejecting asset. The disclaimer fires here, before the rejection-reason
     * form opens, rather than at the final submit — otherwise it would pop up on
     * top of that form instead of being asked up front. */
    const onRejectHide = () => setDisplayRejection(false);
    const onRejectShow = () => guardReject(() => setDisplayRejection(true));

    const onRejectSubmit = async (reissuedStatus, rejectionReason) => {
        try {
            setButtonLoading(true);
            const next = {
                id: data.id,
                isApproved: false,
                status: reissuedStatus ? 1 : data.status,
                isAssigned: data.isAssigned,
                reissuedStatus,
                rejectionReason,
                updatedBy: auth.account.id
            };
            await dispatch(actions.auditAssets.create.patchRequest([next]));
            dispatch(actions.toast.create.success(strings("assetRejected")));
            setDisplayRejection(false);
            setButtonLoading(false);
            patchSuccess();
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On rejecting asset:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onRejectingAsset"))
            );
            setButtonLoading(false);
        }
    };

    /** Upload data for image and exclusions */
    const { uploadData } = useCurrentAssetData();

    const excludeAssetStatus = useMemo(
        () =>
            typesList.assetStatus.data.find((item) => item.name === "excluded"),
        [typesList]
    );
    /** On exclusion Reason submit */
    const onExclusionSubmit = async () => {
        try {
            const uuid = uuidv4();
            const now = new Date().toISOString();
            const next = {
                ...uploadData,
                auditAssetContent: [
                    {
                        ...uploadData.auditAssetContent[0],
                        assetController: {
                            ...uploadData.auditAssetContent[0].assetController,
                            status: excludeAssetStatus.id,
                            exclusionReason: exclusionReason,
                            exclusionReasonDate: exclusionDatePicker
                                ? new Date(selectedDate).toISOString()
                                : null
                        },
                        notes: [
                            {
                                content: exclusionNote,
                                createdDate: now
                            }
                        ]
                    }
                ]
            };
            /** data for the controller  redux*/
            const mergeData = {
                assetControllerAuxiliary: [],
                assignedDate:
                    uploadData.auditAssetContent[0].assetController
                        .assignedDate,
                auditAsset:
                    uploadData.auditAssetContent[0].assetController.auditAsset,
                auditImages: [],
                auditNotes: [
                    {
                        assetController: uuid,
                        content: exclusionNote,
                        createdDate: now,
                        id: uuid
                    }
                ],
                auditServices: [],
                controller: auth.account.id,
                exclusionReason: exclusionReason,
                id: uuid,
                exclusionReasonDate: exclusionDatePicker
                    ? new Date(selectedDate).toISOString()
                    : null
            };
            //Submit exclusion details
            await dispatch(actions.auditControllers.create.postRequest(next));
            //Add the new controller data to Redux store (avoids direct mutation)
            await dispatch(
                actions.auditControllers.create.updateAsset(mergeData)
            );

            setExclusionReason(null);
            setExclusionNote("");

            //Update the corresponding auditAsset to mark it as excluded
            if (exclusionReason) {
                await dispatch(
                    actions.auditAssets.create.updateAsset({
                        auditAssetId: parseInt(auditAssetId),
                        status: enums.status.assetStatus.excluded 
                    })
                );
            }
            dispatch(
                actions.toast.create.success(
                    exclusionReason
                        ? strings("assetExcluded")
                        : strings("messages.noteAdded")
                )
            );
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On exclusion submission:", { error });
            dispatch(
                actions.toast.create.error(
                    strings("errors.onExclusionSubmission")
                )
            );
        }
    };

    const onHistorySubmit = async () => {
        try {
            const uuid = uuidv4();
            const now = new Date().toISOString();

            const next = {
                ...uploadData,
                auditAssetContent: [
                    {
                        ...uploadData.auditAssetContent[0],
                        assetController: {
                            ...uploadData.auditAssetContent[0].assetController
                        },
                        notes: [
                            {
                                content: historyNote,
                                createdDate: now
                            }
                        ]
                    }
                ]
            };

            const mergeData = {
                assetControllerAuxiliary: [],
                assignedDate:
                    uploadData.auditAssetContent[0].assetController.assignedDate,
                auditAsset:
                    uploadData.auditAssetContent[0].assetController.auditAsset,
                auditImages: [],
                auditNotes: [
                    {
                        assetController: uuid,
                        content: historyNote,
                        createdDate: now,
                        id: uuid
                    }
                ],
                auditServices: [],
                controller: auth.account.id,
                exclusionReason:
                    uploadData.auditAssetContent[0].assetController
                        ?.exclusionReason || null,
                id: uuid,
                exclusionReasonDate:
                    uploadData.auditAssetContent[0].assetController
                        ?.exclusionReasonDate || null
            };

            await dispatch(actions.auditControllers.create.postRequest(next));
            await dispatch(actions.auditControllers.create.updateAsset(mergeData));

            setHistoryNote("");
            dispatch(actions.toast.create.success(strings("messages.noteAdded")));
        } catch (error) {
            console.error(error);
            logToSentry("On history submission:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onHistorySubmission"))
            );
        }
    };
    useEffect(() => {
        const getAsset = async () => {
            if (isSharedAsset) {
                const res = validatedVisibility.data.find(
                    (item) =>
                        item.auditAsset === isSharedAsset.sharedValidationAsset
                );

                if (res) setValidatedAsset(res);
                if (!res) {
                    const { assetAuditor } = await dispatch(
                        actions.validatedVisibility.create.getRequest(
                            isSharedAsset.sharedValidationAsset
                        )
                    );
                    setValidatedAsset(assetAuditor[0]);
                }
            }
        };
        getAsset();
    }, [isSharedAsset]);

    if (loading) return <CircularProgress sx={styles.loadingContainer} />;

    /** If no asset is found display not found message */
    if (data && Object.keys(data.asset).length === 0)
        return <NotFound message={strings("messages.assetNotFound")} />;

    return (
        data && (
            <>
                {/* Actions of toolbar moved as action component */}
                <Toolbar
                    onNext={handleNavigate(false)}
                    onPrevious={handleNavigate(true)}
                    value={data.id}
                    values={auditReview.auditAssets.ids}
                />
                <PendingCountApprovalDialog {...dialogProps} />
                <Layout
                    LeftComponent={
                        <LeftComponent
                            assetAuditor={assetAuditor}
                            setAssetAuditor={setAssetAuditor}
                            data={data}
                            exclusionReason={exclusionReason}
                            historyNote={historyNote}
                            setExclusionReason={setExclusionReason}
                            setHistoryNote={setHistoryNote}
                            setNoteType={setNoteType}
                            exclusionNote={exclusionNote}
                            setExclusionNote={setExclusionNote}
                            onExclusionSubmit={onExclusionSubmit}
                            onHistorySubmit={onHistorySubmit}
                            exclusionDatePicker={exclusionDatePicker}
                            handleDateChange={handleDateChange}
                            selectedDate={selectedDate}
                        />
                    }
                    RightComponent={
                        <>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                    justifyContent: "space-between",
                                    p: 1
                                }}
                            ></Box>

                            <RightComponent
                                assetAuditor={assetAuditor}
                                displayRejection={displayRejection}
                                onRejectHide={onRejectHide}
                                onRejectSubmit={onRejectSubmit}
                                loading={buttonLoading}
                                setButtonLoading={setButtonLoading}
                                readonly={readonly}
                                data={data}
                                validatedAsset={validatedAsset}
                                uploadData={uploadData}
                                exclusionReason={exclusionReason}
                                setExclusionReason={setExclusionReason}
                                exclusionNote={exclusionNote}
                                setNoteType={setNoteType}
                                setExclusionNote={setExclusionNote}
                                onExclusionSubmit={onExclusionSubmit}
                                onHistorySubmit={onHistorySubmit}
                                exclusionDatePicker={exclusionDatePicker}
                                handleDateChange={handleDateChange}
                                selectedDate={selectedDate}
                            />
                        </>
                    }
                    ActionComponent={
                        <ActionComponent
                            approved={data && data.isApproved === true}
                            rejected={data && data.isApproved === false}
                            data={data}
                            loading={buttonLoading}
                            readonly={readonly}
                            displayRejection={displayRejection}
                            onApprove={onApprove}
                            onRejectHide={onRejectHide}
                            onRejectShow={onRejectShow}
                        />
                    }
                />
            </>
        )
    );
};

//TODO
// 1.Correct controller merging data - FIXED
// there are discussions going on including the exclusion reason bug
// fix this when we have final things sorted
